/**
 * useProfileChange — BACKLOG-1675 (Bill 2026-10-02).
 *
 * Runs the existing-customer profile-change ceremony at a save choke point:
 *
 *   begin(person, ctx)  →  preview against live Core Director
 *                       →  no change? resolve straight through
 *                       →  change? open ProfileChangeConfirmModal and await the officer
 *                       →  Confirm: apply (CustMod + waterfall + notes), stamp the
 *                          person with the applied key, resolve
 *                       →  Not now / Cancel: resolve with applied=false
 *
 * `begin` returns a promise so the caller can `await` the officer's decision
 * inline: `const o = await begin(saved, {...}); if (!o.proceed) return; save(o.person)`.
 * Render `modal` once in the host's tree.
 *
 * Fail-soft on preview: when Core Director cannot be compared, the caller
 * proceeds with a visible warning (the commit-time CIF update still runs because
 * no applied key is stamped). Fail-closed on apply: an error keeps the modal
 * open with the message; nothing is stamped.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  ProfileChangeApplyResponse,
  ProfileChangePreviewResponse,
  ProfileChangeSource,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { RaoPerson } from '@/modules/RAO/types';
import { ProfileChangeConfirmModal, type ProfileChangeConfirmParams } from './ProfileChangeConfirmModal';
import {
  personToProfileChangeInput,
  readProfileChangeMode,
  stampProfileChange,
  summarizeApply,
} from './profileChange';

export interface ProfileChangeNotice {
  tone: 'ok' | 'warn' | 'err';
  text: string;
}

export interface ProfileChangeOutcome {
  /** False when the officer backed out at the confirm step. */
  proceed: boolean;
  /** The person, stamped with the applied key when Core Director was written. */
  person: RaoPerson;
  applied: boolean;
  notice?: ProfileChangeNotice;
}

export interface ProfileChangeContext {
  source: ProfileChangeSource;
  cartId?: string;
  /** cart: edits also live on an application; cif: standalone CIF edit. */
  surface: 'cart' | 'cif';
}

interface ModalState {
  person: RaoPerson;
  cifId: string;
  preview: ProfileChangePreviewResponse;
  ctx: ProfileChangeContext;
}

export interface UseProfileChangeResult {
  begin: (person: RaoPerson, ctx: ProfileChangeContext) => Promise<ProfileChangeOutcome>;
  /** True while the preview request is in flight (before the modal opens). */
  comparing: boolean;
  modal: JSX.Element | null;
}

export function useProfileChange(): UseProfileChangeResult {
  const client = useApiClient();
  const { config } = useConfig();
  const [state, setState] = useState<ModalState | null>(null);
  const [comparing, setComparing] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [refreshedNotice, setRefreshedNotice] = useState<string | null>(null);
  const resolverRef = useRef<((o: ProfileChangeOutcome) => void) | null>(null);

  const mode = readProfileChangeMode(config.systemSettings);

  const finish = useCallback((outcome: ProfileChangeOutcome): void => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setApplying(false);
    setApplyError(null);
    setRefreshedNotice(null);
    resolve?.(outcome);
  }, []);

  const begin = useCallback(
    async (person: RaoPerson, ctx: ProfileChangeContext): Promise<ProfileChangeOutcome> => {
      const cifId = (person.cifId ?? '').trim();
      if (!cifId || mode !== 'confirm') return { proceed: true, person, applied: false };

      setComparing(true);
      let preview: ProfileChangePreviewResponse;
      try {
        preview = await client.retail.profileChange.preview(cifId, {
          person: personToProfileChangeInput(person),
          source: ctx.source,
          ...(ctx.cartId ? { cartId: ctx.cartId } : {}),
        });
      } catch (err) {
        setComparing(false);
        const msg = err instanceof Error ? err.message : String(err);
        // Test gate (Bill 2026-10-17): the server refuses a real customer while maint.testCustomersOnly is on. Say so
        // plainly — it is not an outage.
        if ((err as { code?: string } | null)?.code === 'MAINT_TEST_CUSTOMERS_ONLY') {
          return { proceed: false, person, applied: false, notice: { tone: 'err', text: msg } };
        }
        return {
          proceed: true, person, applied: false,
          notice: {
            tone: 'warn',
            text: ctx.surface === 'cart'
              ? `Core Director could not be reached to compare this customer's record (${msg}). The application was saved; the customer record is updated when the account opens.`
              : `Core Director could not be reached to compare this customer's record (${msg}). Nothing was written.`,
          },
        };
      }
      setComparing(false);

      if (!preview.success) {
        return {
          proceed: ctx.surface === 'cart', person, applied: false,
          notice: {
            tone: preview.errorCode === 'CIF_NOT_FOUND' ? 'err' : 'warn',
            text: preview.error ?? 'Core Director did not return this customer, so the change could not be compared.',
          },
        };
      }
      if (!preview.hasChanges) return { proceed: true, person, applied: false };

      return new Promise<ProfileChangeOutcome>((resolve) => {
        resolverRef.current = resolve;
        setApplyError(null);
        setRefreshedNotice(null);
        setState({ person, cifId, preview, ctx });
      });
    },
    [client, mode],
  );

  const onCancel = useCallback((): void => {
    if (!state) return;
    finish({
      proceed: true, person: state.person, applied: false,
      notice: state.ctx.surface === 'cart'
        ? { tone: 'warn', text: 'Saved to the application only. Core Director was not updated; the customer record is updated when the account opens.' }
        : { tone: 'warn', text: 'No changes were sent to Core Director.' },
    });
  }, [state, finish]);

  const onConfirm = useCallback(
    async (params: ProfileChangeConfirmParams): Promise<void> => {
      if (!state) return;
      setApplying(true);
      setApplyError(null);
      try {
        // A 409 PREVIEW_STALE arrives as a thrown ApiError (code = the body's errorCode); treat it like the response
        // below so the comparison is refreshed instead of showing a raw "Not saved" (Bill 2026-10-17 support report).
        const resp: ProfileChangeApplyResponse = await client.retail.profileChange.apply(state.cifId, {
          person: personToProfileChangeInput(state.person),
          authMethodCode: params.authMethodCode,
          customerInitials: params.customerInitials,
          accounts: params.accounts.map((a) => ({ acctId: a.acctId, acctType: a.acctType })),
          previewToken: state.preview.previewToken,
          source: state.ctx.source,
          ...(state.ctx.cartId ? { cartId: state.ctx.cartId } : {}),
        }).catch((e: unknown) => {
          if ((e as { code?: string } | null)?.code === 'PREVIEW_STALE') {
            return { success: false, errorCode: 'PREVIEW_STALE', error: e instanceof Error ? e.message : String(e) } as unknown as ProfileChangeApplyResponse;
          }
          throw e;
        });

        if (!resp.success) {
          if (resp.errorCode === 'PREVIEW_STALE') {
            // Someone changed the record meanwhile — re-compare and show it again.
            try {
              const fresh = await client.retail.profileChange.preview(state.cifId, {
                person: personToProfileChangeInput(state.person),
                source: state.ctx.source,
                ...(state.ctx.cartId ? { cartId: state.ctx.cartId } : {}),
              });
              if (fresh.success && fresh.hasChanges) {
                setState({ ...state, preview: fresh });
                setRefreshedNotice('Core Director\'s record changed since this review opened. The comparison was refreshed — check it again before confirming.');
                setApplying(false);
                return;
              }
              if (fresh.success && !fresh.hasChanges) {
                finish({ proceed: true, person: state.person, applied: false,
                  notice: { tone: 'ok', text: 'Core Director already holds these values; nothing needed to change.' } });
                return;
              }
            } catch { /* fall through to the error banner */ }
          }
          setApplyError(resp.error ?? 'Core Director did not accept the change.');
          setApplying(false);
          return;
        }

        const stamped = resp.applied ? stampProfileChange(state.person, resp, params.authMethodCode) : state.person;
        const notice: ProfileChangeNotice = resp.applied
          ? summarizeApply(resp)
          : { tone: 'warn', text: resp.warnings[0] ?? 'Nothing was written to Core Director.' };
        finish({ proceed: true, person: stamped, applied: resp.applied, notice });
      } catch (err) {
        setApplyError(err instanceof Error ? err.message : String(err));
        setApplying(false);
      }
    },
    [state, client, finish],
  );

  const modal = useMemo((): JSX.Element | null => {
    if (!state) return null;
    const name = `${state.person.firstName ?? ''} ${state.person.lastName ?? ''}`.trim();
    return (
      <ProfileChangeConfirmModal
        open
        cifId={state.cifId}
        {...(name ? { customerName: name } : {})}
        preview={state.preview}
        context={state.ctx.surface}
        applying={applying}
        applyError={applyError}
        refreshedNotice={refreshedNotice}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    );
  }, [state, applying, applyError, refreshedNotice, onCancel, onConfirm]);

  return { begin, comparing, modal };
}
